import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { isAuthApiError, type Session } from '@supabase/supabase-js';
import { useQueryClient } from '@tanstack/react-query';
import { AuthContext, type AuthContextValue } from '@/hooks/auth-context';
import { isFocusRefreshExempt } from '@/constants';
import {
  supabase,
  tryRefreshSession,
  logger,
  setUserContext,
  clearUserContext,
  signOutAdmin,
  clearMemberPhotoCache,
  type SignOutAdminOptions,
} from '@/lib';

/**
 * A tab hidden for less than this comes back without refetching anything — an
 * alt-tab to WhatsApp and back shouldn't reload every query on the page.
 */
export const FOCUS_REFETCH_MIN_HIDDEN_MS = 30_000;

/**
 * Owns the admin session for the whole app. Mounted once in App.tsx; every
 * `useAuth()` call reads it from context.
 *
 * This used to live inside `useAuth` itself, so each of the dozens of mounted
 * callers (route guards, Layout, every `usePermissions`) ran its own auth
 * subscription, idle timer and tab-focus listener. One tab switch fired five or
 * more concurrent token refreshes — the exact rotation race `tryRefreshSession`
 * exists to prevent on Safari < 15.4 — and as many whole-cache invalidations.
 *
 * The idle timeout is deliberately NOT here: this provider also wraps public
 * pages (booth display, schedule), which must never lock or sign out the
 * shared session. The protected route guards run it — see useAdminIdleTimeout.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  // Who the React Query cache currently belongs to. `undefined` until the first
  // session read, so the initial load never counts as an identity switch. A
  // DIFFERENT user arriving later — a PIN fast-switch in another tab synced
  // here by the SDK, or the next person signing in after a sign-out (which
  // resets this to null) — drops the previous admin's cache.
  const cacheOwnerRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    const adoptSession = (next: Session | null) => {
      const userId = next?.user?.id ?? null;
      const owner = cacheOwnerRef.current;
      if (owner !== undefined && userId && owner !== userId) {
        queryClient.clear();
        clearMemberPhotoCache();
      }
      cacheOwnerRef.current = userId;

      setSession(next);

      // Sentry user context for production error tracking
      if (next?.user) {
        setUserContext({ id: next.user.id, email: next.user.email || undefined });
      } else {
        clearUserContext();
      }
    };

    const initializeAuth = async () => {
      try {
        const { data: { session: initial }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          // Only log in development to avoid exposing sensitive info in production
          if (process.env.NODE_ENV === 'development') {
            logger.error('Error getting initial session:', sessionError);
          }
          setError(new Error('Failed to initialize authentication'));
          return;
        }

        adoptSession(initial);
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          logger.error('Unexpected error during auth initialization:', err);
        }
        setError(err instanceof Error ? err : new Error('Authentication initialization failed'));
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, next) => {
      try {
        if (process.env.NODE_ENV === 'development') {
          logger.info('Auth state change', { event });
        }

        // Clearing happens on sign-IN only (inside adoptSession), never on
        // sign-out: on sign-out the protected tree is still mounted, so
        // clearing there makes every dashboard observer refetch straight into
        // the session being revoked and file a pile of `anon` 42501s. See
        // `signOutAdmin`.
        adoptSession(next);
        setError(null);
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          logger.error('Error handling auth state change:', err);
        }
        setError(err instanceof Error ? err : new Error('Authentication state change failed'));
      } finally {
        setIsLoading(false);
      }
    });

    // Re-validate the session when the tab regains focus. Supabase's built-in
    // auto-refresh is throttled or paused while the tab is hidden, so a user
    // returning after a long absence could otherwise browse with a dead JWT.
    //
    // React Query's refetchOnWindowFocus/refetchOnReconnect are disabled in
    // react-query-client.ts so they can't race this and fail against a dead
    // connection; after a good re-validation this refetches instead.
    let hiddenAt: number | null = null;
    const onVisibilityChange = async () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now();
        return;
      }
      if (document.visibilityState !== 'visible') return;

      const hiddenFor = hiddenAt === null ? 0 : Date.now() - hiddenAt;
      hiddenAt = null;

      const { data: cached } = await supabase.auth.getSession();
      if (!cached.session) return; // not signed in – nothing to validate

      // Rotates only when the token is near expiry, and shares one in-flight
      // refresh with any edge-function call doing the same.
      const refresh = await tryRefreshSession();
      if (!refresh.ok) {
        // Only the auth server rejecting the refresh token ends the session. A
        // network failure (a laptop waking before Wi-Fi is back) keeps it: the
        // next query or edge call retries the refresh on its own.
        if (isAuthApiError(refresh.error)) {
          if (process.env.NODE_ENV === 'development') {
            logger.warn('Session refresh rejected on tab focus; signing out', {
              error: String(refresh.error),
            });
          }
          await supabase.auth.signOut({ scope: 'local' }).catch(() => {});
        } else if (process.env.NODE_ENV === 'development') {
          logger.warn('Session refresh failed on tab focus (transient); keeping session', {
            error: String(refresh.error),
          });
        }
        return;
      }

      if (hiddenFor < FOCUS_REFETCH_MIN_HIDDEN_MS) return;
      queryClient.invalidateQueries({
        predicate: (query) => !isFocusRefreshExempt(query.queryKey),
      });
    };

    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      subscription.unsubscribe();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [queryClient]);

  const signOut = useCallback(async (options?: SignOutAdminOptions) => {
    try {
      await signOutAdmin(options);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        logger.error('Unexpected error during sign out:', err);
      }
      throw err instanceof Error ? err : new Error('Sign out failed');
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ session, user: session?.user ?? null, isLoading, error, signOut }),
    [session, isLoading, error, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
